import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { LeadService } from '../../services/lead.service';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { Lead, Opportunity, ConvertLeadDTO } from '../../interfaces/crm/crm.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';

interface LeadConvertDialogProps {
  open: boolean;
  lead: Lead;
  onClose: () => void;
  onConverted: (opportunity: Opportunity) => void;
}

type Mode = 'new' | 'existing';

const LeadConvertDialog: React.FC<LeadConvertDialogProps> = ({ open, lead, onClose, onConverted }) => {
  const toast = useToast();

  const [mode, setMode] = useState<Mode>('new');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [existingCustomerId, setExistingCustomerId] = useState('');

  // New-customer fields (pre-filled from the lead per the spec default mapping).
  const [customerName, setCustomerName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [taxRegistrationNo, setTaxRegistrationNo] = useState('');

  // Opportunity fields.
  const [opportunityTitle, setOpportunityTitle] = useState('');
  const [estimatedAmount, setEstimatedAmount] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');

  const [saving, setSaving] = useState(false);

  // Re-seed defaults each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const defaultCustomerName = lead.companyName || lead.name;
    setMode('new');
    setExistingCustomerId('');
    setCustomerName(defaultCustomerName);
    setContactPerson(lead.name);
    setEmail(lead.email ?? '');
    setPhone(lead.phone ?? '');
    setAddress('');
    setTaxRegistrationNo('');
    setOpportunityTitle(defaultCustomerName);
    setEstimatedAmount('');
    setExpectedCloseDate('');
  }, [open, lead]);

  // Load customers for the existing-customer picker when the dialog opens.
  useEffect(() => {
    if (!open) return;
    const institutionId = AuthService.getSelectedInstitution();
    CustomerService.listByInstitution(institutionId, 0, 200)
      .then(res => setCustomers(res.content))
      .catch(e => toast.showApiError(e, 'Failed to load customers'));
  }, [open, toast]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const customerResolved = mode === 'existing'
    ? existingCustomerId !== ''
    : customerName.trim() !== '';
  const canSubmit = customerResolved && opportunityTitle.trim() !== '' && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const amount = estimatedAmount.trim() === '' ? undefined : Number(estimatedAmount);
      const dto: ConvertLeadDTO = {
        opportunityTitle: opportunityTitle.trim(),
        estimatedAmount: Number.isFinite(amount as number) ? amount : undefined,
        expectedCloseDate: expectedCloseDate || undefined,
        ...(mode === 'existing'
          ? { existingCustomerId }
          : {
              customerName: customerName.trim(),
              contactPerson: contactPerson.trim() || undefined,
              email: email.trim() || undefined,
              phone: phone.trim() || undefined,
              address: address.trim() || undefined,
              taxRegistrationNo: taxRegistrationNo.trim() || undefined,
            }),
      };
      const opp = await LeadService.convert(lead.id, dto);
      toast.showSuccess('Lead converted');
      onConverted(opp);
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to convert lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" data-help-page="crm.leadConvert.page">
      <DialogTitle>Convert Lead</DialogTitle>
      <DialogContent>
        <Box data-help-section="crm.leadConvert.section.main" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl>
            <FormLabel>Customer</FormLabel>
            <RadioGroup
              data-help="crm.leadConvert.field.mode"
              row
              value={mode}
              onChange={e => setMode(e.target.value as Mode)}
            >
              <FormControlLabel data-help="crm.leadConvert.field.modeNew" value="new" control={<Radio />} label="New customer" />
              <FormControlLabel data-help="crm.leadConvert.field.modeExisting" value="existing" control={<Radio />} label="Existing customer" />
            </RadioGroup>
          </FormControl>

          {mode === 'existing' ? (
            <SearchableSelect
              data-help="crm.leadConvert.field.existingCustomer"
              label="Customer"
              required
              options={customers}
              value={existingCustomerId}
              onChange={id => setExistingCustomerId(id)}
              getOptionLabel={c => c.name}
            />
          ) : (
            <>
              <TextField
                data-help="crm.leadConvert.field.customerName"
                label="Customer Name"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                required
              />
              <TextField
                data-help="crm.leadConvert.field.contactPerson"
                label="Contact Person"
                value={contactPerson}
                onChange={e => setContactPerson(e.target.value)}
              />
              <TextField
                data-help="crm.leadConvert.field.email"
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
              <TextField
                data-help="crm.leadConvert.field.phone"
                label="Phone"
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
              <TextField
                data-help="crm.leadConvert.field.address"
                label="Address"
                value={address}
                onChange={e => setAddress(e.target.value)}
                multiline
                minRows={2}
              />
              <TextField
                data-help="crm.leadConvert.field.taxNo"
                label="Tax Registration No."
                value={taxRegistrationNo}
                onChange={e => setTaxRegistrationNo(e.target.value)}
              />
            </>
          )}

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Opportunity</Typography>
          <TextField
            data-help="crm.leadConvert.field.title"
            label="Opportunity Title"
            value={opportunityTitle}
            onChange={e => setOpportunityTitle(e.target.value)}
            required
          />
          <TextField
            data-help="crm.leadConvert.field.amount"
            label="Estimated Amount"
            type="number"
            value={estimatedAmount}
            onChange={e => setEstimatedAmount(e.target.value)}
            inputProps={{ step: 0.0001, min: 0 }}
          />
          <TextField
            data-help="crm.leadConvert.field.closeDate"
            label="Expected Close Date"
            type="date"
            value={expectedCloseDate}
            onChange={e => setExpectedCloseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="crm.leadConvert.cancel" onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button data-help="crm.leadConvert.save" variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {saving ? <CircularProgress size={20} /> : 'Convert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadConvertDialog;
